import {
  Button,
  Checkbox,
  FormGroup,
  HTMLSelect,
  Intent,
  Menu,
  MenuItem,
  NumericInput,
  Popover,
  Position,
  TextArea,
} from "@blueprintjs/core";
import {
  CompanyAddressType,
  UpdateCompanyAddressPayload,
} from "../../../types/ApiTypes";
import { formatAddress } from "../../../helpers/AddressHelper";
import { gql, useMutation } from "@apollo/client";
import { useState } from "react";
import { DefaultToaster } from "../../shared/DefaultToaster/DefaultToaster";

type UpdateAddressParams = {
  payload: UpdateCompanyAddressPayload;
};

type UpdateAddressResult = {
  updateCompanyAddress: {
    id: number;
  };
};

const UpdateAddress = gql`
  mutation UpdateAddress($payload: UpdateCompanyAddressPayloadInput!) {
    updateCompanyAddress(payload: $payload) {
      id
    }
  }
`;

type UpdateAddressSiteInformationPayload = {
  addressId: number;
  hasDock?: boolean;
  numberOfInsideStairs?: number;
  numberOfOutsideStairs?: number;
  hasFreightElevator?: boolean;
  hasPassengerElevator?: boolean;
  isCertificateOfInsuranceRequired?: boolean;
};

const UpdateAddressSiteInformation = gql`
  mutation UpdateAddressSiteInformation(
    $payload: UpdateAddressSiteInformationPayloadInput!
  ) {
    updateAddressSiteInformation(payload: $payload) {
      id
    }
  }
`;

export type CompanyAddressItemProps = {
  companyAddress: {
    id: number;
    type: CompanyAddressType;
    companyId: number;
    address: {
      id: number;
      street1: string;
      street2: string;
      city: string;
      state: string;
      postalCode: string;
      notes: string;
      addressSiteInformation?: {
        id: number;
        hasDock: boolean;
        numberOfInsideStairs: number;
        numberOfOutsideStairs: number;
        hasFreightElevator: boolean;
        hasPassengerElevator: boolean;
        isCertificateOfInsuranceRequired: boolean;
      };
    };
  };
  onRemove: () => void;
  onChange: (payload: UpdateCompanyAddressPayload) => void;
};

export function CompanyAddressItem({
  companyAddress,
  onChange,
  onRemove,
}: CompanyAddressItemProps) {
  const [insideStairs, setInsideStairs] = useState<string>(
    companyAddress.address.addressSiteInformation?.numberOfInsideStairs.toString() ??
      "0"
  );
  const [outsideStairs, setOutsideStairs] = useState<string>(
    companyAddress.address.addressSiteInformation?.numberOfOutsideStairs.toString() ??
      "0"
  );
  const [hasLoadingDock, setHasLoadingDock] = useState<boolean>(
    companyAddress.address.addressSiteInformation?.hasDock ?? false
  );
  const [hasPassengerElevator, setHasPassengerElevator] = useState<boolean>(
    companyAddress.address.addressSiteInformation?.hasPassengerElevator ?? false
  );
  const [hasFreightElevator, setHasFreightElevator] = useState<boolean>(
    companyAddress.address.addressSiteInformation?.hasFreightElevator ?? false
  );
  const [certificateOfInsuranceRequired, setCertificateOfInsuranceRequired] =
    useState<boolean>(
      companyAddress.address.addressSiteInformation
        ?.isCertificateOfInsuranceRequired ?? false
    );
  const [notes, setNotes] = useState<string>(
    companyAddress.address.notes ?? ""
  );

  const [updateAddressSiteInformation] = useMutation<
    { updateAddressSiteMutation: { id: number } },
    { payload: UpdateAddressSiteInformationPayload }
  >(UpdateAddressSiteInformation);

  const [updateAddress] = useMutation<UpdateAddressResult, UpdateAddressParams>(
    UpdateAddress
  );

  async function saveNotes() {
    await updateAddress({
      variables: {
        payload: {
          id: companyAddress.id,
          notes,
        },
      },
    });

    DefaultToaster.show({
      message: "Address notes saved!",
      intent: "success",
      icon: "tick-circle",
    });
  }

  return (
    <div className={"divide-y border-b"}>
      <div className="p-2 flex">
        <div className="flex-1">
          <address
            className="not-italic"
            dangerouslySetInnerHTML={{
              __html: formatAddress(companyAddress.address).replaceAll(
                "\n",
                "<br />"
              ),
            }}
          />
        </div>
        <HTMLSelect
          value={companyAddress.type.toString()}
          onChange={(e) =>
            onChange({
              id: companyAddress.id,
              type: e.target.value as CompanyAddressType,
            })
          }
          className="mr-2"
        >
          <option value="">Choose Type</option>
          <option value="Primary">Primary</option>
          <option value="Billing">Billing</option>
          <option value="Fulfillment">Fulfillment</option>
          <option value="Other">Other</option>
        </HTMLSelect>
        <Popover position={Position.BOTTOM_RIGHT} minimal>
          <Button minimal icon="more" />
          <Menu>
            <MenuItem
              text="Remove from Company"
              icon="minus"
              intent={Intent.DANGER}
              onClick={() => onRemove()}
            />
          </Menu>
        </Popover>
      </div>

      <div className="p-2 bg-gray-50 space-y-2">
        <div className="space-y-1">
          <FormGroup label="Notes" style={{ margin: 0 }}>
            <TextArea
              fill
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </FormGroup>

          <Button small text="Save Changes" onClick={saveNotes} />
        </div>

        {companyAddress.type === CompanyAddressType.Fulfillment && (
          <>
            <h4 className="font-medium uppercase">Site Information</h4>
            <div className="flex space-x-2">
              <FormGroup label="# Inside Stairs" className="flex-1">
                <NumericInput
                  value={insideStairs}
                  onValueChange={async (e, v) => {
                    setInsideStairs(v);

                    await updateAddressSiteInformation({
                      variables: {
                        payload: {
                          addressId: companyAddress.address.id,
                          numberOfInsideStairs: parseInt(v),
                        },
                      },
                    });
                  }}
                  stepSize={1}
                  minorStepSize={1}
                  majorStepSize={1}
                  min={0}
                  fill
                />
              </FormGroup>
              <FormGroup label="# Outside Stairs" className="flex-1">
                <NumericInput
                  value={outsideStairs}
                  onValueChange={async (e, v) => {
                    setOutsideStairs(v);

                    await updateAddressSiteInformation({
                      variables: {
                        payload: {
                          addressId: companyAddress.address.id,
                          numberOfOutsideStairs: parseInt(v),
                        },
                      },
                    });
                  }}
                  stepSize={1}
                  minorStepSize={1}
                  majorStepSize={1}
                  min={0}
                  fill
                />
              </FormGroup>
            </div>
            <Checkbox
              label="Has Loading Dock"
              checked={hasLoadingDock}
              onChange={async (e) => {
                const checked = (e.target as any).checked;
                setHasLoadingDock(checked);

                await updateAddressSiteInformation({
                  variables: {
                    payload: {
                      addressId: companyAddress.address.id,
                      hasDock: checked,
                    },
                  },
                });
              }}
            />
            <Checkbox
              label="Has Freight Elevator"
              checked={hasFreightElevator}
              onChange={async (e) => {
                const checked = (e.target as any).checked;
                setHasFreightElevator(checked);

                await updateAddressSiteInformation({
                  variables: {
                    payload: {
                      addressId: companyAddress.address.id,
                      hasFreightElevator: checked,
                    },
                  },
                });
              }}
            />
            <Checkbox
              label="Has Passenger Elevator"
              checked={hasPassengerElevator}
              onChange={async (e) => {
                const checked = (e.target as any).checked;
                setHasPassengerElevator(checked);

                await updateAddressSiteInformation({
                  variables: {
                    payload: {
                      addressId: companyAddress.address.id,
                      hasPassengerElevator: checked,
                    },
                  },
                });
              }}
            />
            <Checkbox
              label="Certificate of Insurance Required"
              checked={certificateOfInsuranceRequired}
              onChange={async (e) => {
                const checked = (e.target as any).checked;
                setCertificateOfInsuranceRequired(checked);

                await updateAddressSiteInformation({
                  variables: {
                    payload: {
                      addressId: companyAddress.address.id,
                      isCertificateOfInsuranceRequired: checked,
                    },
                  },
                });
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
